import { Link } from "react-router-dom";
import { Users, Cog, ArrowRight, Luggage, MapPin, Baby } from "lucide-react";
import { fmtMoney, fmtDate } from "@/lib/api";
import { useCurrency } from "@/context/CurrencyContext";

const FALLBACK =
  "https://images.unsplash.com/photo-1601315639340-ce7dd5bf78f5?crop=entropy&cs=srgb&fm=jpg&q=85";

export default function VehicleCard({ vehicle, testid, query, bestValue = false, weekendNote = null, freeBabySeat = false }) {
  const { currency, convert } = useCurrency();
  return (
    <div
      data-testid={testid || `vehicle-card-${vehicle.id}`}
      className="group bg-card border border-border rounded-2xl overflow-hidden transition-transform duration-300 hover:-translate-y-1 hover:border-primary/40"
    >
      <Link to={`/fleet/${vehicle.id}`} className="block relative aspect-[16/10] overflow-hidden">
        <img
          src={vehicle.image_url || FALLBACK}
          alt={vehicle.name}
          onError={(e) => (e.currentTarget.src = FALLBACK)}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          loading="lazy"
        />
        <span className="absolute top-4 left-4 text-[10px] font-ui tracking-[0.2em] uppercase bg-black/60 backdrop-blur-md border border-border rounded-full px-3 py-1.5 text-primary">
          {vehicle.category}
        </span>
        {bestValue && (
          <span data-testid={`best-value-badge-${vehicle.id}`} className="absolute top-[3.75rem] left-4 text-[10px] font-ui font-semibold tracking-[0.2em] uppercase bg-amber-400 text-[#1A1A1A] rounded-full px-3 py-1.5 shadow-[0_4px_14px_rgba(251,191,36,0.45)]">
            {vehicle.badge || "Best Value"}
          </span>
        )}
        {freeBabySeat && (
          <span
            data-testid={`free-baby-seat-badge-${vehicle.id}`}
            className={`absolute ${bestValue ? "top-[6.75rem]" : "top-[3.75rem]"} left-4 inline-flex items-center gap-1.5 text-[10px] font-ui font-semibold tracking-[0.2em] uppercase bg-emerald-500 text-white rounded-full px-3 py-1.5 shadow-[0_4px_14px_rgba(16,185,129,0.45)]`}
          >
            <Baby size={11} /> FREE baby seat — 12+ days
          </span>
        )}
        {vehicle.lot_status && (
          <span
            data-testid={`vehicle-lot-status-${vehicle.id}`}
            className={`absolute top-4 right-4 text-[10px] font-ui tracking-[0.15em] uppercase backdrop-blur-md border rounded-full px-3 py-1.5 ${
              vehicle.lot_status === "on_lot"
                ? "bg-emerald-500/80 border-emerald-300/40 text-white"
                : vehicle.lot_status === "rented"
                  ? "bg-amber-500/85 border-amber-300/40 text-white"
                  : "bg-black/60 border-border text-white/80"
            }`}
          >
            {vehicle.lot_status === "on_lot" ? "On lot today" : vehicle.lot_status === "rented" ? `Rented · back ${fmtDate(vehicle.available_date)}` : "Off lot"}
          </span>
        )}
      </Link>
      <div className="p-6">
        <div className="flex items-start justify-between gap-4">
          <h3 className="font-display text-2xl tracking-tight">
            <Link to={`/fleet/${vehicle.id}`} className="hover:text-primary transition-colors duration-200">{vehicle.name}</Link>
          </h3>
          <div className="text-right shrink-0">
            <p className="gold-text font-ui font-semibold text-lg" data-testid={`vehicle-price-${vehicle.id}`}>
              {fmtMoney(vehicle.price_per_day)} <span className="text-[10px] text-muted-foreground font-normal">USD</span>
            </p>
            {currency !== "USD" && (
              <p data-testid={`vehicle-price-converted-${vehicle.id}`} className="text-[11px] text-muted-foreground font-ui">≈ {convert(vehicle.price_per_day)}</p>
            )}
            <p className="text-[11px] text-muted-foreground font-ui">per day</p>
          </div>
        </div>
        <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-sm sm:text-xs text-muted-foreground font-ui">
          <span className="flex items-center gap-1.5"><Users size={13} className="gold-text" /> {vehicle.seats} seats</span>
          <span data-testid={`vehicle-luggage-${vehicle.id}`} className="flex items-center gap-1.5"><Luggage size={13} className="gold-text" /> Fits {vehicle.luggage ?? 0} bags</span>
          <span className="flex items-center gap-1.5"><Cog size={13} className="gold-text" /> {vehicle.transmission}</span>
        </div>
        {vehicle.features?.length > 0 && (
          <p className="mt-3 text-sm sm:text-xs text-muted-foreground line-clamp-1">{vehicle.features.join(" · ")}</p>
        )}
        {vehicle.location_label && (
          <p data-testid={`vehicle-location-${vehicle.id}`} className="mt-2 flex items-center gap-1.5 text-sm sm:text-xs font-ui text-emerald-700">
            <MapPin size={12} className="shrink-0" /> {vehicle.location_label}{vehicle.location_note ? ` — ${vehicle.location_note}` : ""}
          </p>
        )}
        {weekendNote && (
          <p data-testid={`vehicle-scarcity-${vehicle.id}`} className="mt-2 flex items-center gap-1.5 text-sm sm:text-xs font-ui font-semibold text-amber-700">
            <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse shrink-0" /> {weekendNote}
          </p>
        )}
        <Link to={`/book/${vehicle.id}${query || ""}`} className="block mt-6">
          <button
            data-testid={`book-vehicle-button-${vehicle.id}`}
            className="w-full flex items-center justify-center gap-2 rounded-full border border-primary/40 text-primary py-2.5 text-sm font-ui tracking-wide transition-colors duration-200 hover:bg-primary hover:text-primary-foreground"
          >
            Book Now <ArrowRight size={15} />
          </button>
        </Link>
        <div data-testid={`payment-trust-strip-${vehicle.id}`} className="mt-3.5 flex items-center justify-center gap-1.5">
          <span className="text-[9px] font-ui tracking-[0.15em] uppercase text-muted-foreground/70 mr-1">We accept</span>
          <span className="rounded-md border border-border bg-white px-2 py-0.5 text-[9px] font-bold italic text-[#1A1F71] tracking-wide">VISA</span>
          <span className="rounded-md border border-border bg-white px-2 py-0.5 flex items-center" aria-label="Mastercard"><span className="w-2.5 h-2.5 rounded-full bg-[#EB001B] inline-block" /><span className="w-2.5 h-2.5 rounded-full bg-[#F79E1B] inline-block -ml-1" /></span>
          <span className="rounded-md border border-border bg-white px-2 py-0.5 text-[9px] font-bold italic text-[#003087]">PayPal</span>
          <span className="rounded-md border border-border bg-white px-2 py-0.5 text-[9px] font-semibold text-[#3C4043]">G&nbsp;Pay</span>
        </div>
        <Link
          data-testid={`vehicle-detail-link-${vehicle.id}`}
          to={`/fleet/${vehicle.id}`}
          className="mt-3 block text-center text-xs font-ui text-muted-foreground hover:text-primary transition-colors duration-200"
        >
          View details & availability →
        </Link>
      </div>
    </div>
  );
}
